import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  getDialogs, getMessages, sendMessage, sendMediaGroup, uploadMedia,
  getChannels, getSessions,
  markDialogRead, editMessage, deleteMessage, deleteDialog,
  subscribeDialogEvents, subscribeDialogsList,
  loadDialogHistory, sendTyping,
} from '../api.js';
import { buildMessagesByExternalId } from '../utils/replyLookup.mjs';

const CHANNEL_ICONS = {
  telegram_bot: '\u{1F916}',
  telegram_personal: '\u{2708}️',
  whatsapp_business: '\u{1F4AC}',
  whatsapp_personal: '\u{1F4F1}',
  max_bot: '\u{1F50B}',
  max_personal: '\u{1F4E8}',
};

const CHANNEL_LABELS = {
  telegram_bot: 'Telegram — бот',
  telegram_personal: 'Telegram — личный',
  whatsapp_business: 'WhatsApp — бизнес',
  whatsapp_personal: 'WhatsApp — личный',
  max_bot: 'MAX — бот',
  max_personal: 'MAX — личный',
};


function timeAgo(iso) {
  if (!iso) return '';

  const diff = (
    Date.now()
    - new Date(iso).getTime()
  );

  const mins = Math.floor(
    diff / 60000
  );

  if (mins < 1) return 'сейчас';
  if (mins < 60) return `${mins} мин`;

  const hrs = Math.floor(
    mins / 60
  );

  if (hrs < 24) return `${hrs} ч`;

  const days = Math.floor(
    hrs / 24
  );

  return `${days} д`;
}


function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Human-readable placeholder for media-only messages without text.
// Without this, the UI showed `[empty]` for stickers, polls, system events,
// etc. — confusing operators who can't tell what the sender actually sent.
const _MEDIA_LABELS = {
  photo:    '📷 Фото',
  video:    '🎬 Видео',
  voice:    '🎤 Голосовое сообщение',
  audio:    '🎵 Аудио',
  document: '📎 Документ',
  sticker:  '😀 Стикер',
  location: '📍 Геолокация',
  contact:  '📇 Контакт',
  poll:     '📊 Опрос',
  other:    '📦 Вложение',
};
function mediaPlaceholder(type) {
  if (!type) return null;
  return _MEDIA_LABELS[type] || '[Вложение]';
}


function mediaFileName(message) {
  const explicitName = String(
    message?.media_file_name || '',
  ).trim();

  if (explicitName) return explicitName;

  const mediaUrl = String(
    message?.media_url || '',
  ).trim();

  if (!mediaUrl) return null;

  try {
    const parsed = new URL(
      mediaUrl,
      window.location.origin,
    );

    const encodedBaseName = (
      parsed.pathname.split('/').pop() || ''
    );

    const baseName = decodeURIComponent(
      encodedBaseName,
    );

    // New HUB media objects use:
    // <32-char UUID>_<original safe filename>
    const originalName = baseName.replace(
      /^[0-9a-f]{32}_/i,
      '',
    );

    if (
      originalName
      && originalName !== baseName
    ) {
      return originalName;
    }
  } catch {
    return null;
  }

  return null;
}

// Time windows (seconds) for editing/deleting own messages, per channel.
// Only personal sessions support edit/delete in current implementation —
// bot channels (telegram_bot, whatsapp_business, max_bot) need their own
// adapter methods, not wired yet. Setting their windows to 0 hides the UI
// buttons and matches backend behavior (which rejects with 400).
const EDIT_WINDOW_SEC = {
  whatsapp_personal: 15 * 60,
  telegram_personal: 48 * 3600,
  // Bot channels — not implemented:
  telegram_bot: 48 * 3600,
  whatsapp_business: 0,
  max_personal:      Infinity,
  max_bot: 7 * 24 * 3600,
};
const DELETE_WINDOW_SEC = {
  whatsapp_personal: 2 * 24 * 3600,
  telegram_personal: Infinity,
  // Bot channels — not implemented:
  telegram_bot: 48 * 3600,
  whatsapp_business: 0,
  max_personal:      Infinity,
  max_bot: Infinity,
};

function ageSec(iso) {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / 1000;
}

function canEdit(msg, channelType) {
  // Pending operations block re-edit (avoid races)
  if (msg.delivery_state === 'edit_pending' || msg.delivery_state === 'delete_pending') return false;
  if (msg.delivery_state === 'sending' || msg.delivery_state === 'failed') return false;
  const limit = EDIT_WINDOW_SEC[channelType] ?? 0;
  // Use sent_at when available (actual messenger ack) — falls back to created_at
  const reference = msg.sent_at || msg.created_at;
  return limit > 0 && ageSec(reference) < limit;
}

function canDelete(msg, channelType) {
  if (msg.delivery_state === 'edit_pending' || msg.delivery_state === 'delete_pending') return false;
  // sending: too risky (worker may be about to send) — don't show button
  if (msg.delivery_state === 'sending') return false;
  // Failed messages: always deletable (local-only — never reached messenger)
  if (msg.delivery_state === 'failed') return true;
  const limit = DELETE_WINDOW_SEC[channelType] ?? 0;
  const reference = msg.sent_at || msg.created_at;
  return limit > 0 && ageSec(reference) < limit;
}

// Visual indicator for delivery state next to each outgoing message.
// Important: dialog.last_read_at is OPERATOR read time, not client read time.
// For Telegram Bot we normally cannot know if the human recipient read the
// bot message, so we show only "sent" unless backend supplies recipient_read_at.
function deliveryIndicator(state, msg) {
  switch (state) {
    case 'sending':
      return {
        icon: '\u{1F551}',
        title: 'Отправляется…',
        color: '#a0aec0',
      };

    case 'edit_pending':
      return {
        icon: '\u{270F}\u{FE0F}',
        title: 'Изменение отправляется…',
        color: '#3182ce',
      };

    case 'delete_pending':
      return {
        icon: '\u{1F5D1}',
        title: 'Удаление отправляется…',
        color: '#dd6b20',
      };

    case 'failed':
      return {
        icon: '\u{274C}',
        // Prefer the humanized text (tells the operator what to DO); the raw
        // messenger error stays as fallback so nothing is lost when the
        // backend has no mapping for that code.
        title: (
          msg?.delivery_error_text
          || msg?.delivery_error
          || 'Не удалось отправить'
        ),
        color: '#e53e3e',
      };

    case 'sent': {
      if (
        msg?.recipient_read_at
        || msg?.recipient_read_state === 'read'
      ) {
        return {
          icon: '\u{2713}\u{2713}',
          title: 'Прочитано получателем',
          color: '#3182ce',
        };
      }

      return {
        icon: '\u{2713}',
        title: (
          'Отправлено. Статус прочтения '
          + 'для этого канала неизвестен.'
        ),
        color: '#a0aec0',
      };
    }

    case 'deleted':
      return null;

    default:
      return null;
  }
}


function groupMessagesForDisplay(items) {
  const output = [];
  const groups = new Map();

  for (const msg of items) {
    const albumCompatible = (
      Boolean(msg.media_group_id)
      && ['photo', 'video'].includes(msg.media_type)
      && !msg.deleted_at
      && msg.delivery_state !== 'deleted'
    );

    if (!albumCompatible) {
      output.push(msg);
      continue;
    }

    const key = `${msg.direction}:${msg.media_group_id}`;
    let grouped = groups.get(key);

    if (!grouped) {
      grouped = {
        ...msg,
        album_items: [msg],
        // Keep the real row that owns the Telegram album caption.
        // The API returns newest messages first, so the synthetic album
        // representative may otherwise point at a different media item.
        album_caption_item: msg.text ? msg : null,
      };

      groups.set(key, grouped);
      output.push(grouped);
      continue;
    }

    grouped.album_items.push(msg);

    // Telegram normally puts the caption on the first item, but retaining
    // the first non-empty value protects imported and legacy payloads too.
    if (!grouped.album_caption_item && msg.text) {
      grouped.album_caption_item = msg;
    }

    if (!grouped.text && msg.text) {
      grouped.text = msg.text;
    }

    if (!grouped.sender_name && msg.sender_name) {
      grouped.sender_name = msg.sender_name;
    }
  }

  return output;
}


const getChannelFamily = (channelType = "") => {
  const value = String(channelType || "").toLowerCase();

  if (value.includes("telegram")) return "telegram";
  if (value.includes("whatsapp")) return "whatsapp";
  if (value.includes("max")) return "max";

  return "default";
};

const isBotChannelType = (channelType = "") => {
  const value = String(channelType || "").toLowerCase();
  return value.includes("bot");
};

const ChannelGlyph = ({ family, size = 20 }) => {
  if (family === "telegram") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M20.665 3.717a1.2 1.2 0 0 0-1.234-.17L3.923 9.604c-.84.337-.8 1.553.06 1.832l3.95 1.285 1.53 4.774a1.2 1.2 0 0 0 2.05.42l2.185-2.243 4.284 3.144a1.2 1.2 0 0 0 1.898-.69l1.933-13.1a1.2 1.2 0 0 0-.548-1.309ZM9.33 12.28l7.69-5.23-5.986 6.132a.9.9 0 0 0-.23.42l-.68 2.628-.794-2.477a.9.9 0 0 0-.577-.577l-2.477-.804 3.054-1.092Z"
          fill="white"
        />
      </svg>
    );
  }

  if (family === "whatsapp") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M20.52 11.47c0 4.72-3.82 8.54-8.53 8.54-1.5 0-2.91-.39-4.13-1.08L3 20.3l1.39-4.72a8.48 8.48 0 0 1-.93-4.11c0-4.71 3.82-8.53 8.53-8.53 4.71 0 8.53 3.82 8.53 8.53Z"
          fill="white"
        />
        <path
          d="M15.87 13.56c-.2-.1-1.2-.6-1.39-.67-.19-.07-.33-.1-.47.1-.14.2-.54.67-.66.81-.12.14-.24.16-.44.05-.2-.1-.85-.31-1.62-.98-.6-.54-1-1.2-1.12-1.4-.12-.2-.01-.31.09-.41.09-.09.2-.24.3-.36.1-.12.14-.2.21-.34.07-.14.03-.26-.02-.36-.05-.1-.47-1.13-.64-1.55-.17-.41-.35-.35-.47-.35h-.4c-.14 0-.36.05-.55.26-.19.2-.72.7-.72 1.71s.74 1.99.85 2.12c.1.14 1.45 2.22 3.52 3.11.49.21.88.33 1.18.42.5.16.95.13 1.3.08.4-.06 1.2-.49 1.37-.97.17-.48.17-.89.12-.97-.05-.09-.19-.14-.39-.24Z"
          fill="#25D366"
        />
      </svg>
    );
  }

  if (family === "max") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" fill="white" fillOpacity="0.18" />
        <path
          d="M7.2 16.8V7.2h2.1l2.7 3.42L14.7 7.2h2.1v9.6h-2.13v-6.1L12 14.15l-2.67-3.45v6.1H7.2Z"
          fill="white"
        />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="white" fillOpacity="0.25" />
    </svg>
  );
};

const ChannelAvatar = ({ channelType, size = 42 }) => {
  const family = getChannelFamily(channelType);
  const isBot = isBotChannelType(channelType);

  const palette = {
    telegram: {
      bg: "linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)",
      shadow: "0 8px 18px rgba(34, 158, 217, 0.28)",
    },
    whatsapp: {
      bg: "linear-gradient(135deg, #25D366 0%, #1FAA59 100%)",
      shadow: "0 8px 18px rgba(37, 211, 102, 0.28)",
    },
    max: {
      bg: "linear-gradient(135deg, #7B61FF 0%, #5A3FFF 100%)",
      shadow: "0 8px 18px rgba(90, 63, 255, 0.28)",
    },
    default: {
      bg: "linear-gradient(135deg, #94A3B8 0%, #64748B 100%)",
      shadow: "0 8px 18px rgba(100, 116, 139, 0.22)",
    },
  };

  const theme = palette[family] || palette.default;

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: theme.bg,
          boxShadow: theme.shadow,
          border: "1px solid rgba(255,255,255,0.55)",
        }}
      >
        <ChannelGlyph family={family} size={Math.round(size * 0.5)} />
      </div>

      {isBot ? (
        <div
          style={{
            position: "absolute",
            right: -2,
            bottom: -2,
            padding: "2px 5px",
            borderRadius: 999,
            background: "#0F172A",
            color: "#fff",
            fontSize: 9,
            fontWeight: 800,
            lineHeight: 1,
            letterSpacing: 0.4,
            border: "2px solid #fff",
            boxShadow: "0 4px 10px rgba(15, 23, 42, 0.28)",
          }}
        >
          BOT
        </div>
      ) : null}
    </div>
  );
};

const getChannelIcon = (channelType, size = 42) => (
  <ChannelAvatar channelType={channelType} size={size} />
);

export default function Messenger({ currentUser }) {
  const isAdmin = currentUser?.role === 'admin';
  const [dialogs, setDialogs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);

  const displayMessages = useMemo(
    () => groupMessagesForDisplay(messages),
    [messages],
  );

  const [recording, setRecording] = useState(false);
  const [voiceDraft, setVoiceDraft] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [channels, setChannels] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [newChat, setNewChat] = useState({ chat_id: '', channel_id: '' });
  // Bitrix24 Open Lines owns employee assignment and queue distribution.
  // Hub Messenger is a technical admin view and always shows tenant dialogs.
  // Edit mode: when set, the bottom input becomes the editor for this message.
  // Delete confirmation: id of the message awaiting confirm (one at a time).
  const [editingMessage, setEditingMessage] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedReply, setSelectedReply] = useState(null);
  // Toast for inline error/success notifications (no browser alert).
  const [toast, setToast] = useState(null); // { text, kind: "error"|"info" }
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const mediaChunksRef = useRef([]);
  const mediaStreamRef = useRef(null);
  const discardVoiceRecordingRef = useRef(false);
  // Track whether the user is "stuck to bottom" — if they've scrolled up,
  // we stop auto-scrolling on new polls so they don't get yanked away.
  const stickToBottomRef = useRef(true);
  // Per-DIALOG throttle for markDialogRead. Originally a single number
  // (global), but that meant: open A → open B within 5s → B's read was
  // throttled out and the backend never learned B was read (badge in
  // sidebar would reappear on the next list reload, and the peer never
  // saw the read receipt). Keyed by dialog id so each dialog has its
  // own 5s window.
  const markReadThrottleRef = useRef(new Map());
  // Track dialogs we've already kicked auto-backfill on this session.
  // Without this, clicking 50 sparse dialogs in a row enqueues 50 worker
  // tasks serialized per session = minutes of waiting for newer messages.
  const autoBackfilledRef = useRef(new Set());
  const _shouldMarkRead = (dialogId) => {
    const now = Date.now();
    const last = markReadThrottleRef.current.get(dialogId) || 0;
    if (now - last < 5000) return false;
    markReadThrottleRef.current.set(dialogId, now);
    return true;
  };

  useEffect(() => {
    setSelectedReply(null);
    setPendingAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedId]);
  // Flag set when user picks a new dialog; reset after we successfully
  // scroll to bottom on the first render with messages present. Without
  // this, scrollIntoView fires when messages is still `[]` (we just reset
  // it) so the ref points to nothing — operator lands wherever DOM happens
  // to leave the scrollTop, usually somewhere in the middle.
  const needsScrollToBottomRef = useRef(false);
  // Live "X is typing…" set, keyed by user_id, with value = user_name.
  // Wipe on dialog change so we don't carry typers across switches.
  const [typingUsers, setTypingUsers] = useState({});
  // Throttle outbound typing pings so we don't spam /typing on every keystroke
  const lastTypingPingRef = useRef(0);
  // ID of the previous selected dialog — reset stick-to-bottom on switch.
  const lastSelectedRef = useRef(null);

  const showToast = useCallback((text, kind = 'error') => {
    setToast({ text, kind, id: Date.now() });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  // Load dialogs (scoped). If the polled list shows unread for the currently-open
  // dialog (operator is viewing it), zero it out immediately so the badge
  // doesn't flicker between dialog polls and the next "mark read" tick.
  const loadDialogs = useCallback(() => {
    getDialogs().then((list) => {
      setSelectedId((curId) => {
        if (curId) {
          list = list.map((d) =>
            d.id === curId && d.unread_count > 0 ? { ...d, unread_count: 0 } : d
          );
          // Do not mark read from the dialog-list refresh itself.
          // Read is marked only when the chat is actually opened below.
        }
        setDialogs(list);
        return curId;
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadDialogs();
    // SSE push for dialog-list updates (new message in any dialog →
    // refresh so unread badge updates without delay). Slow polling as
    // fallback in case SSE breaks.
    const es = subscribeDialogsList(() => { loadDialogs(); });
    const t = setInterval(loadDialogs, 60000);
    return () => {
      clearInterval(t);
      try { es.close(); } catch {}
    };
  }, [loadDialogs]);

  // ── Cursor-paginated messages (Telegram-style infinite scroll) ──
  // Initial: load latest 50 on dialog open.
  // Poll every 3s: fetch only NEW messages (`after=last_loaded_at`) and merge.
  // Scroll to top: fetch OLDER 50 (`before=first_loaded_at`) and prepend.
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const loadingOlderRef = useRef(false);

  // Merge incoming messages into state by id (upsert + chronological sort)
  const mergeMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setMessages((curr) => {
      const byId = new Map(curr.map((m) => [m.id, m]));
      incoming.forEach((m) => byId.set(m.id, m));  // overwrites = handles edits
      return Array.from(byId.values()).sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });
  }, []);

  // O(1) lookup of quoted messages for the reply-block render. Without
  // this the JSX inline IIFE did messages.find() per reply per render —
  // O(N) × number of replies × every keystroke (text input triggers re-
  // render). At 200 messages with 50 replies that's 10k comparisons per
  // keystroke. useMemo with messages dep keeps it cheap.
  const messagesByExternalId = useMemo(
    () => buildMessagesByExternalId(messages),
    [messages],
  );

  // History backfill is only meaningful for personal channels (GramJS /
  // Baileys can call `client.getMessages(..., {limit, offsetId})`). The
  // Telegram Bot API physically can't deliver messages that arrived
  // before the user pressed Start on the bot — calling load-history for
  // a bot channel always 400s. Skip silently for non-personal so
  // operators don't see spurious 400s in Network/Console every time
  // they open a bot dialog.
  // Narrower than "personal": only the Telegram worker implements the
  // load_history command. MAX and WhatsApp Personal workers drop it as an
  // unknown command, so asking them produced a silent no-op — a spinner, no
  // new messages, no error. The backend refuses these too; keeping the sets
  // aligned means the operator never sees the request fail.
  const HISTORY_BACKFILL_CHANNEL_TYPES = new Set(['telegram_personal']);
  const supportsHistoryBackfill = (dialog) =>
    HISTORY_BACKFILL_CHANNEL_TYPES.has(dialog?.channel?.channel_type);

  // Initial load + SSE push + slow polling fallback
  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    setMessages([]);
    setHasMoreOlder(true);
    loadingOlderRef.current = false;

    // Initial fetch — latest 50. If our local copy is thin (we've only
    // seen a few live messages since the worker connected), kick off a
    // backfill from TG so the operator sees real history instead of just
    // "the 5 messages that arrived since we started watching". Threshold
    // 20 picked empirically — under that, it's almost always worth pulling
    // the full last page from Telegram. The backfill is async; SSE pushes
    // the new rows as they arrive, so the UI fills in below.
    //
    // Bot-only / WA-Business / MAX-Bot channels skipped — see
    // `supportsHistoryBackfill` comment above.
    const dialogForBackfill = dialogs.find((d) => d.id === selectedId);
    const canBackfill = supportsHistoryBackfill(dialogForBackfill);
    getMessages(selectedId, { limit: 50 }).then((msgs) => {
      if (cancelled) return;
      setMessages(msgs);
      if (msgs.length < 50) setHasMoreOlder(false);
      if (canBackfill && msgs.length < 20 && !autoBackfilledRef.current.has(selectedId)) {
        autoBackfilledRef.current.add(selectedId);
        loadDialogHistory(selectedId, { limit: 50 }).catch(() => { /* worker offline / race — silent */ });
      }
    }).catch(() => {});

    // Incremental refresh — fetches anything newer than the last we have.
    // Every post-await side-effect is guarded by `cancelled`: if the user
    // switched dialogs while we were awaiting, we must NOT mark the prior
    // dialog read or touch the dialog list with the stale selectedId.
    // Also: only mark read when we actually received incoming messages, so
    // we're not amplifying writes on outgoing-only echoes.
    const refreshIncremental = async () => {
      if (cancelled) return;
      let after;
      setMessages((curr) => { after = curr.length ? curr[curr.length - 1].created_at : undefined; return curr; });
      let newMsgs = [];
      try {
        const opts = { limit: 50 };
        if (after) opts.after = after;
        newMsgs = await getMessages(selectedId, opts);
      } catch {}
      if (cancelled) return;
      if (newMsgs.length > 0) mergeMessages(newMsgs);
      const hasIncoming = newMsgs.some((m) => m.direction === 'INCOMING' || m.direction === 'incoming');
      // Mark read ONLY when (a) genuinely new incoming arrived, and
      // (b) we haven't told the backend in the last 5s. Without throttling,
      // every SSE event (edits, deletes, outgoing echoes) triggered a /read
      // POST → admin_rate_limit (30/min) returned 429.
      if (hasIncoming) {
        // If the operator is already inside this chat, the message is visible.
        // Mark it read locally, but only throttled and only for the opened chat.
        if (_shouldMarkRead(selectedId)) {
          markDialogRead(selectedId).catch(() => {});
          setDialogs((prev) => prev.map((d) =>
            d.id === selectedId && d.unread_count > 0 ? { ...d, unread_count: 0 } : d
          ));
        }
      }
    };

    // Initial mark-read + immediate local badge clear. This is REQUIRED
    // independently of refreshIncremental's incoming-guarded path: when the
    // operator just clicks back into a dialog they already viewed, no new
    // messages arrive but the badge in the sidebar must drop to 0 anyway.
    markDialogRead(selectedId).catch(() => {});
    markReadThrottleRef.current.set(selectedId, Date.now());  // baseline
    setDialogs((prev) => prev.map((d) =>
      d.id === selectedId && d.unread_count > 0 ? { ...d, unread_count: 0 } : d
    ));

    // Reset typing UI when switching dialogs
    setTypingUsers({});

    // SSE: push notification on new/edited/deleted messages + typing.
    const es = subscribeDialogEvents(selectedId, (ev) => {
      if (ev?.type === 'typing') {
        // Don't show self-typing
        if (ev.user_id === currentUser?.id) return;
        setTypingUsers((prev) => {
          if (ev.is_typing) {
            return { ...prev, [ev.user_id]: { name: ev.user_name, ts: Date.now() } };
          }
          const next = { ...prev };
          delete next[ev.user_id];
          return next;
        });
        return;
      }
      refreshIncremental();
    });

    // Sweep stale typers every 5s (no "stop" event came → user closed tab)
    const typingSweep = setInterval(() => {
      setTypingUsers((prev) => {
        const now = Date.now();
        const next = {};
        for (const [uid, info] of Object.entries(prev)) {
          if (now - info.ts < 8000) next[uid] = info;
        }
        return next;
      });
    }, 3000);

    // Slow polling fallback (30s) — covers the case where SSE silently
    // breaks behind a proxy that strips Connection: keep-alive.
    const t = setInterval(refreshIncremental, 30000);

    return () => {
      cancelled = true;
      clearInterval(t);
      clearInterval(typingSweep);
      try { es.close(); } catch {}
    };
  }, [selectedId, mergeMessages, currentUser?.id]);

  // Load older messages on scroll-to-top
  const loadOlder = useCallback(async () => {
    if (!selectedId || loadingOlderRef.current || !hasMoreOlder) return;
    loadingOlderRef.current = true;
    let before;
    setMessages((curr) => { before = curr.length ? curr[0].created_at : undefined; return curr; });
    if (!before) { loadingOlderRef.current = false; return; }
    try {
      const container = messagesContainerRef.current;
      const oldScrollHeight = container?.scrollHeight || 0;
      const older = await getMessages(selectedId, { limit: 50, before });
      if (older.length === 0) {
        // Nothing more in our DB — but TG might still have older history.
        // Fire a backfill (worker pulls from TG into our DB, SSE pushes
        // each new row back to us). User stays put; messages fade in.
        //
        // Bot channels can't backfill (Bot API doesn't return pre-/start
        // history) — short-circuit so we don't issue a doomed 400.
        // Read `dialogs` via setDialogs((curr) => curr) so we see the
        // latest state without adding it to the useCallback deps (which
        // would re-create the callback every poll cycle).
        setHasMoreOlder(false);
        let dialogNow = null;
        setDialogs((curr) => { dialogNow = curr.find((d) => d.id === selectedId); return curr; });
        if (supportsHistoryBackfill(dialogNow)) {
          try {
            // Find the oldest tg_personal_NNN id we have to pass as
            // before_message_id, so worker fetches strictly older.
            let oldestTgId = null;
            setMessages((curr) => {
              for (const m of curr) {
                const ext = m.external_message_id || '';
                if (ext.startsWith('tg_personal_')) {
                  const n = parseInt(ext.slice('tg_personal_'.length), 10);
                  if (!isNaN(n) && (oldestTgId === null || n < oldestTgId)) oldestTgId = n;
                }
              }
              return curr;
            });
            await loadDialogHistory(selectedId, {
              limit: 50,
              beforeMessageId: oldestTgId || null,
            });
          } catch (e) { /* worker offline / race — silent */ }
        }
      } else {
        mergeMessages(older);
        if (older.length < 50) setHasMoreOlder(false);
        // Preserve visual scroll position after prepending
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - oldScrollHeight;
          }
        });
      }
    } catch {}
    loadingOlderRef.current = false;
  }, [selectedId, hasMoreOlder, mergeMessages]);

  // ── Edit/Delete handlers — inline UX like Telegram ──
  // Edit: load text into bottom input, show banner above with original preview.
  // Delete: show inline "confirm?" UI on the message row (no browser confirm).

  const handleEditMessage = useCallback((msg) => {
    setEditingMessage(msg);
    setText(msg.text || '');
    setConfirmDeleteId(null);
    // Focus input after state flush
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMessage(null);
    setText('');
  }, []);

  const handleDeleteMessage = useCallback((msg) => {
    // Two-step: first click → ask confirm inline; second click → commit
    setConfirmDeleteId(msg.id);
    setEditingMessage(null);
  }, []);

  const confirmDelete = useCallback(async (msg) => {
    setConfirmDeleteId(null);
    try {
      await deleteMessage(selectedId, msg.id);
      // Merge — don't replace whole list (would lose loaded-older history)
      getMessages(selectedId, { limit: 50 }).then(mergeMessages).catch(() => {});
    } catch (err) {
      showToast(`Не удалось удалить: ${err.message}`);
    }
  }, [selectedId, mergeMessages, showToast]);

  const cancelDelete = useCallback(() => setConfirmDeleteId(null), []);

  // Esc cancels active edit/delete
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (editingMessage) cancelEdit();
        if (confirmDeleteId) cancelDelete();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingMessage, confirmDeleteId, cancelEdit, cancelDelete]);

  // Auto-scroll behavior: jump to bottom when:
  //   1) operator opens a new dialog (reset stick-to-bottom = true)
  //   2) a new message arrives AND user is already at/near bottom
  // If user has scrolled up to read history, polls do NOT yank them down.
  useEffect(() => {
    // New dialog selected — mark "needs scroll" and bail. The actual
    // scrollIntoView happens on the next pass once messages are populated.
    if (selectedId !== lastSelectedRef.current) {
      lastSelectedRef.current = selectedId;
      stickToBottomRef.current = true;
      needsScrollToBottomRef.current = true;
      return;
    }
    // First render with messages after a dialog switch — scroll to bottom.
    // rAF defers until the DOM has actually laid out the new rows,
    // otherwise scrollHeight is wrong and we land somewhere mid-list.
    if (needsScrollToBottomRef.current && messages.length > 0) {
      needsScrollToBottomRef.current = false;
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
      return;
    }
    // Same dialog, messages updated — scroll only if user is still near bottom
    if (stickToBottomRef.current) {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      }
    }
  }, [messages, selectedId]);

  // Track scroll position: decide auto-scroll behavior + trigger load-older
  const onMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 80;  // 80px threshold
    // Near the top — trigger infinite scroll
    if (el.scrollTop < 100) loadOlder();
  }, [loadOlder]);

  // Tick every 30s so edit/delete buttons hide once the window expires,
  // even if no new messages arrive. State is used only to trigger re-render.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Load channels for new chat
  useEffect(() => {
    if (showNewChat) {
      getChannels().then(setChannels).catch(() => {});
      getSessions().then(setSessions).catch(() => {});
    }
  }, [showNewChat]);

  const selectedDialog = dialogs.find((d) => d.id === selectedId);
  const selectedChannelActive =
    selectedDialog?.channel?.is_active !== false;

  const handleDeleteDialog = async () => {
    if (!isAdmin || !selectedDialog) return;

    const label =
      selectedDialog.contact_name
      || selectedDialog.chat_title
      || selectedDialog.external_chat_id;

    const first = window.confirm(
      `Удалить чат «${label}» из Связки?\n\n`
      + 'Подключённый аккаунт и контакт останутся.'
    );

    if (!first) return;

    const second = window.confirm(
      'Подтвердите окончательное удаление.\n\n'
      + 'Все сообщения этого чата будут удалены из Связки '
      + 'безвозвратно. Контакт сохранится.'
    );

    if (!second) return;

    try {
      await deleteDialog(selectedDialog.id);

      setSelectedId(null);
      setMessages([]);
      setText('');
      setSelectedReply(null);
      setEditingMessage(null);
      setConfirmDeleteId(null);
      setPendingAttachments([]);
      setVoiceDraft(null);

      loadDialogs();
      showToast('Чат удалён');
    } catch (err) {
      showToast(
        `Не удалось удалить чат: ${err?.message || err}`
      );
    }
  };


  const guessMediaType = (file, forced = null) => {
    if (forced) return forced;
    const type = file?.type || '';
    if (type.startsWith('image/')) return 'photo';
    if (type.startsWith('video/')) return 'video';
    if (type.startsWith('audio/')) return 'audio';
    return 'document';
  };

  const formatFileSize = (bytes = 0) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const refreshAfterSend = async () => {
    const msgs = await getMessages(selectedId, { limit: 50 });
    mergeMessages(msgs);
    loadDialogs();
  };

  const uploadMediaFile = async (
    file,
    forcedMediaType = null,
  ) => {
    const mediaType = guessMediaType(file, forcedMediaType);

    const uploaded = await uploadMedia({
      file,
      file_name: file.name || (
        mediaType === 'voice' ? 'voice.webm' : 'file'
      ),
      media_type: mediaType,
    });

    return {
      media_url: uploaded.media_url,
      media_type: uploaded.media_type || mediaType,
      file_name: uploaded.file_name || file.name || 'file',
      media_duration_seconds:
        uploaded.media_duration_seconds || null,
    };
  };

  const uploadAndSendMedia = async (
    file,
    forcedMediaType = null,
    caption = '',
    replyToExternalId = null,
  ) => {
    if (!selectedDialog) {
      throw new Error('Диалог не выбран');
    }

    const uploaded = await uploadMediaFile(
      file,
      forcedMediaType,
    );

    await sendMessage({
      dialog_id: selectedDialog.id,
      chat_id: selectedDialog.external_chat_id,
      channel_id: selectedDialog.channel.id,
      text: caption,
      media_url: uploaded.media_url,
      media_type: uploaded.media_type,
      media_file_name: uploaded.file_name,
      media_duration_seconds:
        uploaded.media_duration_seconds,
      reply_to_external_id: replyToExternalId,
    });
  };

  // Immediate media path used by recorded voice messages.
  const sendMediaFile = async (file, forcedMediaType = null) => {
    if (!selectedDialog) return;

    if (editingMessage) {
      showToast('Сначала завершите редактирование сообщения');
      return;
    }

    if (!file) return;

    setSending(true);
    setUploadingMedia(true);

    try {
      await uploadAndSendMedia(
        file,
        forcedMediaType,
        text.trim() || '',
        selectedReply?.external_message_id || null,
      );

      setText('');
      setSelectedReply(null);
      await refreshAfterSend();
    } catch (err) {
      showToast('Ошибка отправки медиа: ' + (err?.message || err));

      if (selectedId) {
        try {
          const msgs = await getMessages(selectedId, { limit: 50 });
          mergeMessages(msgs);
        } catch {}
      }
    } finally {
      setUploadingMedia(false);
      setSending(false);
    }
  };

  // File picker only fills the queue. Nothing is uploaded until Send.
  const handleFilePick = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (!files.length) return;

    const queued = files.map((file) => ({
      id: globalThis.crypto?.randomUUID?.()
        || `${Date.now()}-${Math.random()}-${file.name}`,
      file,
    }));

    setPendingAttachments((current) => [
      ...current,
      ...queued,
    ]);
  };

  const removePendingAttachment = (attachmentId) => {
    if (sending || uploadingMedia) return;

    setPendingAttachments((current) =>
      current.filter((item) => item.id !== attachmentId)
    );
  };

  const sendPendingAttachments = async () => {
    if (!selectedDialog || pendingAttachments.length === 0) return;

    const queue = [...pendingAttachments];
    const caption = text.trim();
    const replyToExternalId =
      selectedReply?.external_message_id || null;

    setSending(true);
    setUploadingMedia(true);

    let completed = 0;

    const removeCompletedItems = (items) => {
      const completedIds = new Set(
        items.map((item) => item.id)
      );

      setPendingAttachments((current) =>
        current.filter((item) => !completedIds.has(item.id))
      );
    };

    try {
      const allAlbumCompatible = (
        queue.length >= 2
        && queue.every((item) => {
          const mediaType = guessMediaType(item.file);
          return mediaType === 'photo' || mediaType === 'video';
        })
      );

      if (allAlbumCompatible) {
        // Telegram accepts at most 10 elements in one media group.
        // A final one-item remainder falls back to the ordinary media path.
        for (
          let offset = 0;
          offset < queue.length;
          offset += 10
        ) {
          const group = queue.slice(offset, offset + 10);
          const groupCaption = offset === 0 ? caption : '';
          const groupReply = (
            offset === 0 ? replyToExternalId : null
          );

          if (group.length === 1) {
            await uploadAndSendMedia(
              group[0].file,
              null,
              groupCaption,
              groupReply,
            );
          } else {
            const uploadedItems = [];

            for (const item of group) {
              uploadedItems.push(
                await uploadMediaFile(item.file)
              );
            }

            await sendMediaGroup({
              dialog_id: selectedDialog.id,
              channel_id: selectedDialog.channel.id,
              items: uploadedItems,
              text: groupCaption,
              reply_to_external_id: groupReply,
            });
          }

          completed += group.length;
          removeCompletedItems(group);

          if (offset === 0) {
            setText('');
            setSelectedReply(null);
          }
        }
      } else {
        // Documents, audio and incompatible mixed selections retain the
        // proven sequential-send path.
        for (let index = 0; index < queue.length; index += 1) {
          const item = queue[index];

          await uploadAndSendMedia(
            item.file,
            null,
            index === 0 ? caption : '',
            index === 0 ? replyToExternalId : null,
          );

          completed += 1;
          removeCompletedItems([item]);

          if (index === 0) {
            setText('');
            setSelectedReply(null);
          }
        }
      }

      await refreshAfterSend();
    } catch (err) {
      const failedItem = queue[completed];

      showToast(
        `Не удалось отправить файл ${
          failedItem?.file?.name || completed + 1
        }: ${err?.message || err}`
      );

      if (selectedId) {
        try {
          const messages = await getMessages(
            selectedId,
            { limit: 50 },
          );
          mergeMessages(messages);
        } catch {}
      }
    } finally {
      setUploadingMedia(false);
      setSending(false);
    }
  };

  const stopVoiceRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state !== 'inactive') {
      discardVoiceRecordingRef.current = false;
      recorder.stop();
    }
  };

  const cancelVoiceRecording = () => {
    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state !== 'inactive') {
      discardVoiceRecordingRef.current = true;
      recorder.stop();
    }
  };

  const discardVoiceDraft = () => {
    if (sending || uploadingMedia) return;
    setVoiceDraft(null);
  };

  const sendVoiceDraft = async () => {
    const draft = voiceDraft;

    if (
      !draft
      || !selectedDialog
      || sending
      || uploadingMedia
    ) {
      return;
    }

    setSending(true);
    setUploadingMedia(true);

    try {
      await uploadAndSendMedia(
        draft.file,
        'voice',
        '',
        selectedReply?.external_message_id || null,
      );

      setVoiceDraft(null);
      setSelectedReply(null);
      await refreshAfterSend();
    } catch (err) {
      showToast(
        'Ошибка отправки голосового сообщения: ' + (err?.message || err),
      );

      if (selectedId) {
        try {
          const msgs = await getMessages(
            selectedId,
            { limit: 50 },
          );

          mergeMessages(msgs);
        } catch {}
      }
    } finally {
      setUploadingMedia(false);
      setSending(false);
    }
  };

  const startVoiceRecording = async () => {
    if (!selectedDialog) return;
    if (editingMessage) {
      showToast('Сначала завершите редактирование сообщения');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      showToast('Браузер не поддерживает запись голоса');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      mediaChunksRef.current = [];
      discardVoiceRecordingRef.current = false;

      const preferredTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
      ];
      const mimeType = preferredTypes.find((t) => MediaRecorder.isTypeSupported(t));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          mediaChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const chunks = mediaChunksRef.current || [];
        mediaChunksRef.current = [];
        const actualType = recorder.mimeType || mimeType || 'audio/webm';
        stream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        setRecording(false);

        if (discardVoiceRecordingRef.current) {
          discardVoiceRecordingRef.current = false;
          return;
        }

        if (!chunks.length) {
          showToast('Голосовое пустое');
          return;
        }

        const blob = new Blob(
          chunks,
          { type: actualType },
        );

        const file = new File(
          [blob],
          `voice-${Date.now()}.webm`,
          { type: actualType },
        );

        const previewUrl = URL.createObjectURL(blob);

        setVoiceDraft({
          file,
          url: previewUrl,
        });
      };

      recorder.start();
      setRecording(true);
    } catch (err) {
      setRecording(false);
      try {
        mediaStreamRef.current?.getTracks()?.forEach((track) => track.stop());
      } catch {}
      mediaStreamRef.current = null;
      showToast('Не удалось включить микрофон: ' + (err?.message || err));
    }
  };

  useEffect(() => {
    return () => {
      try {
        if (
          mediaRecorderRef.current
          && mediaRecorderRef.current.state !== 'inactive'
        ) {
          mediaRecorderRef.current.onstop = null;
          mediaRecorderRef.current.stop();
        }
      } catch {}
      try {
        mediaStreamRef.current?.getTracks()?.forEach((track) => track.stop());
      } catch {}
    };
  }, []);

  useEffect(() => {
    return () => {
      if (voiceDraft?.url) {
        URL.revokeObjectURL(voiceDraft.url);
      }
    };
  }, [voiceDraft]);

  useEffect(() => {
    setVoiceDraft(null);
  }, [selectedId]);

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();

    if (!selectedDialog) return;

    if (!editingMessage && pendingAttachments.length > 0) {
      await sendPendingAttachments();
      return;
    }

    if (!trimmed) return;

    setSending(true);
    try {
      if (editingMessage) {
        // Edit mode — call PATCH instead of POST
        if (trimmed === (editingMessage.text || '')) {
          // No change, just exit edit mode
          cancelEdit();
        } else {
          await editMessage(selectedId, editingMessage.id, trimmed);
          setEditingMessage(null);
          setText('');
          // Merge — preserves any older messages loaded via infinite scroll
          const msgs = await getMessages(selectedId, { limit: 50 });
          mergeMessages(msgs);
        }
      } else {
        await sendMessage({
          dialog_id: selectedDialog.id,
          chat_id: selectedDialog.external_chat_id,
          channel_id: selectedDialog.channel.id,
          text: trimmed,
          reply_to_external_id: selectedReply?.external_message_id || null,
        });
        setText('');
        setSelectedReply(null);
        const msgs = await getMessages(selectedId, { limit: 50 });
        mergeMessages(msgs);
        loadDialogs();
      }
    } catch (err) {
      showToast((editingMessage ? 'Редактирование' : 'Отправка') + ': ' + err.message);
      // On a 422 send failure the backend now PERSISTS the row with
      // delivery_state='failed' (so the operator sees ❌ with the actual
      // reason on hover) instead of rolling back. Pull the fresh message
      // list even from the catch branch so the ❌ bubble shows up
      // immediately — otherwise it only appears on the next SSE/refresh.
      //
      // Skip the re-fetch for auth errors — `request()` already dispatched
      // `auth:logout`, so a second 401/403 here would fire it again and
      // race the logout flow. We also can't show new messages to a
      // logged-out user.
      const isAuthErr = err?.message?.startsWith('401') || err?.message?.startsWith('403');
      if (!editingMessage && selectedId && !isAuthErr) {
        try {
          const msgs = await getMessages(selectedId, { limit: 50 });
          mergeMessages(msgs);
        } catch { /* fetch errors here are non-fatal — toast already shown */ }
      }
    }
    setSending(false);
  };

  const handleNewChat = async (e) => {
    e.preventDefault();
    if (!newChat.chat_id.trim() || !newChat.channel_id) return;

    setSending(true);
    try {
      const result = await sendMessage({
        chat_id: newChat.chat_id.trim(),
        channel_id: newChat.channel_id,
        text: text.trim() || 'Hello!',
      });
      setShowNewChat(false);
      setNewChat({ chat_id: '', channel_id: '' });
      setText('');
      loadDialogs();
      // Select the new dialog
      setTimeout(() => {
        setSelectedId(result.dialog_id);
      }, 500);
    } catch (err) {
      showToast('Ошибка: ' + err.message);
    }
    setSending(false);
  };

  return (
    <div className="messenger">
      {/* Left: Dialog list */}
      <div className="msg-sidebar">
        <div className="msg-sidebar-header">
          <h3>Диалоги</h3>
          <button
            className="btn-primary btn-sm"
            onClick={() => setShowNewChat(!showNewChat)}
          >
            {showNewChat ? '✕' : '+'}
          </button>
        </div>

        {showNewChat && (
          <form className="msg-new-chat" onSubmit={handleNewChat}>
            <select
              value={newChat.channel_id}
              onChange={(e) => setNewChat({ ...newChat, channel_id: e.target.value })}
              required
            >
              <option value="">Канал...</option>
              {channels.filter((c) => c.is_active).map((c) => (
                <option key={c.id} value={c.id}>
                  {CHANNEL_ICONS[c.channel_type] || ''} {c.name}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="ID чата / @имя"
              value={newChat.chat_id}
              onChange={(e) => setNewChat({ ...newChat, chat_id: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="Первое сообщение..."
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <button type="submit" className="btn-primary btn-sm" disabled={sending}>
              Отправить
            </button>
          </form>
        )}

        <div className="msg-dialog-list">
          {dialogs.length === 0 && (
            <div className="msg-empty">Диалогов пока нет</div>
          )}
          {dialogs.map((d) => (
            <div
              key={d.id}
              className={`msg-dialog-item ${selectedId === d.id ? 'active' : ''}`}
              onClick={() => { setSelectedId(d.id); setShowNewChat(false); }}
            >
              <div
                className="msg-dialog-avatar"
                style={{
                  background: "transparent",
                  overflow: "visible",
                }}
              >
                {getChannelIcon(d.channel?.channel_type, 38)}
              </div>
              <div className="msg-dialog-info">
                <div className="msg-dialog-top">
                  <span className="msg-dialog-name">
                    {(d.chat_type === 'group' || d.chat_type === 'channel') && (
                      <span title={d.chat_type === 'channel' ? 'Канал' : 'Групповой чат'} style={{ marginRight: 6 }}>
                        {d.chat_type === 'channel' ? '\u{1F4E2}' : '\u{1F465}'}
                      </span>
                    )}
                    {d.contact_name || d.external_chat_id}
                  </span>
                  <span className="msg-dialog-time" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {d.unread_count > 0 && (
                      <span style={{
                        background: '#3182ce', color: '#fff', borderRadius: 12,
                        padding: '1px 8px', fontSize: 11, fontWeight: 600,
                        minWidth: 18, textAlign: 'center',
                      }}>
                        {d.unread_count > 99 ? '99+' : d.unread_count}
                      </span>
                    )}
                    {timeAgo(d.last_message?.created_at)}
                  </span>
                </div>
                {d.chat_type === 'user' && d.contact_name && d.contact_username && (
                  <div className="msg-dialog-chatid">
                    @{d.contact_username}
                  </div>
                )}
                <div className="msg-dialog-preview">
                  {d.last_message?.direction === 'outgoing' && (
                    <span className="msg-preview-you">Вы: </span>
                  )}
                  {d.last_message?.text?.substring(0, 40) || 'Нет сообщений'}
                  {(d.last_message?.text?.length || 0) > 40 ? '...' : ''}
                </div>
                <div className="msg-dialog-channel">
                  {CHANNEL_LABELS[d.channel?.channel_type] || d.channel?.channel_type}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Messages */}
      <div className="msg-chat">
        {selectedDialog ? (
          <>
            <div className="msg-chat-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>
                  {(selectedDialog.chat_type === 'group' || selectedDialog.chat_type === 'channel') && (
                    <span style={{ marginRight: 6 }}>
                      {selectedDialog.chat_type === 'channel' ? '\u{1F4E2}' : '\u{1F465}'}
                    </span>
                  )}
                  {selectedDialog.contact_name || selectedDialog.external_chat_id}
                </strong>
                <span className="msg-chat-channel">
                  {CHANNEL_ICONS[selectedDialog.channel?.channel_type]}{' '}
                  {CHANNEL_LABELS[selectedDialog.channel?.channel_type]}
                  {' — '}
                  {selectedDialog.channel?.name}
                </span>
              </div>

              {isAdmin && (
                <button
                  type="button"
                  onClick={handleDeleteDialog}
                  className="btn-danger btn-sm"
                  title="Удалить этот чат и его сообщения из Связки"
                >
                  Удалить чат
                </button>
              )}
            </div>

            {(selectedDialog.chat_type === 'group' || selectedDialog.chat_type === 'channel') && (
              <div style={{
                background: '#fffaf0', border: '1px solid #fbd38d', borderRadius: 6,
                padding: '10px 14px', margin: '8px 16px 0', fontSize: 13, color: '#7b341e',
              }}>
                <strong>
                  {selectedDialog.chat_type === 'channel' ? '\u{26A0}️ Канал' : '\u{26A0}️ Групповой чат'}
                </strong>
                {': ответы видны всем участникам'}
                {selectedDialog.chat_type === 'channel' && ' / подписчикам'}.
              </div>
            )}

            {!selectedChannelActive && (
              <div
                style={{
                  background: '#f7fafc',
                  borderBottom: '1px solid #e2e8f0',
                  padding: '8px 16px',
                  fontSize: 13,
                  color: '#718096',
                }}
              >
                Аккаунт удалён. История доступна только для чтения.
              </div>
            )}

            <div className="msg-messages" ref={messagesContainerRef} onScroll={onMessagesScroll}>
              {messages.length === 0 && (
                <div className="msg-empty">В этом диалоге пока нет сообщений</div>
              )}
              {displayMessages.map((msg) => {
                const isOut = msg.direction === 'outgoing';
                const isDeleted = !!msg.deleted_at || msg.delivery_state === 'deleted';
                const albumItems = Array.isArray(msg.album_items)
                  ? msg.album_items
                  : [msg];
                const isAlbum = albumItems.length > 1;
                const channelType = selectedDialog.channel?.channel_type;
                // Telegram stores an album caption on one concrete message.
                // Edit that row rather than whichever row became the visual
                // group representative after newest-first API ordering.
                const editTarget = isAlbum
                  ? (msg.album_caption_item || null)
                  : msg;
                const editExternalMessageId =
                  editTarget?.external_message_id;
                const editHasRealId = editExternalMessageId
                  && !editExternalMessageId.startsWith('pending-')
                  && !editExternalMessageId.startsWith('admin-')
                  && !editExternalMessageId.startsWith('out_');
                const isEdited = Boolean(
                  editTarget?.edited_at || msg.edited_at
                );
                const hasRealId = msg.external_message_id
                  && !msg.external_message_id.startsWith('pending-')
                  && !msg.external_message_id.startsWith('admin-')
                  && !msg.external_message_id.startsWith('out_');
                // Edit requires the real messenger id (we address the remote message)
                const maxPersonalAlbum = (
                  isAlbum
                  && channelType === 'max_personal'
                );
                const editable = Boolean(editTarget)
                  && isOut
                  && !isDeleted
                  && !maxPersonalAlbum
                  && editHasRealId
                  && canEdit(editTarget, channelType);
                const deletable = (
                  isOut
                  && !isDeleted
                  && !maxPersonalAlbum
                  && (
                    hasRealId
                    || msg.delivery_state === 'failed'
                  )
                  && canDelete(msg, channelType)
                );
                const indicator = isOut
                  ? deliveryIndicator(msg.delivery_state, msg)
                  : null;
                const dimmed = isDeleted || msg.delivery_state === 'sending'
                  || msg.delivery_state === 'edit_pending' || msg.delivery_state === 'delete_pending';
                return (
                  <div
                    key={msg.id}
                    className={`msg-bubble ${isOut ? 'outgoing' : 'incoming'}`}
                    style={dimmed ? { opacity: 0.7, fontStyle: isDeleted ? 'italic' : 'normal' } : undefined}
                  >
                    {msg.direction === 'incoming' && msg.sender_name && (
                      <div className="msg-sender">{msg.sender_name}</div>
                    )}
                    {msg.forwarded_from && (
                      <div style={{ fontSize: 12, color: '#3182ce', marginBottom: 4, fontStyle: 'italic' }}>
                        ↪ Переслано от {msg.forwarded_from}
                      </div>
                    )}
                    {msg.reply_to_external_id && (() => {
                      const quoted = messagesByExternalId.get(msg.reply_to_external_id);
                      return (
                        <div style={{
                          borderLeft: '3px solid #3182ce',
                          padding: '2px 8px', marginBottom: 6,
                          background: 'rgba(49,130,206,0.08)',
                          borderRadius: 4, fontSize: 12,
                        }}>
                          <div style={{ fontWeight: 600, color: '#3182ce' }}>
                            {quoted?.sender_name || 'Сообщение'}
                          </div>
                          <div style={{ opacity: 0.8 }}>
                            {(quoted?.text || mediaPlaceholder(quoted?.media_type) || '...').slice(0, 100)}
                          </div>
                        </div>
                      );
                    })()}
                    <div className="msg-text">
                      {isDeleted
                        ? '[сообщение удалено]'
                        : (
                          msg.text
                          || (
                            isAlbum
                              ? `📷 Альбом (${albumItems.length})`
                              : mediaPlaceholder(msg.media_type)
                          )
                          || '[без текста]'
                        )}
                    </div>

                    {!isDeleted && isAlbum && (
                      <div
                        className="msg-media"
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          gap: 4,
                          maxWidth: 480,
                          overflow: 'hidden',
                          borderRadius: 9,
                        }}
                      >
                        {albumItems.map((item, index) => {
                          const wideLead = (
                            albumItems.length % 2 === 1
                            && index === 0
                          );

                          const mediaStyle = {
                            width: '100%',
                            height: '100%',
                            minHeight: wideLead ? 220 : 150,
                            maxHeight: wideLead ? 320 : 230,
                            aspectRatio: wideLead ? '16 / 9' : '1 / 1',
                            objectFit: 'cover',
                            display: 'block',
                          };

                          return (
                            <div
                              key={item.id}
                              style={
                                wideLead
                                  ? { gridColumn: '1 / -1' }
                                  : undefined
                              }
                            >
                              {item.media_type === 'video' ? (
                                <video
                                  src={item.media_url}
                                  controls
                                  preload="none"
                                  style={mediaStyle}
                                />
                              ) : (
                                <a
                                  href={item.media_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Открыть в полном размере"
                                  style={{ display: 'block', height: '100%' }}
                                >
                                  <img
                                    src={item.media_url}
                                    alt=""
                                    style={{
                                      ...mediaStyle,
                                      cursor: 'zoom-in',
                                    }}
                                  />
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {!isDeleted && !isAlbum && msg.media_url && (
                      <div className="msg-media">
                        {(['photo', 'sticker'].includes(msg.media_type) || msg.media_type?.startsWith('image')) ? (
                          // Image preview, click → full size in new tab.
                          // max-width/height cap so a 4K photo doesn't blow up the bubble.
                          <a href={msg.media_url} target="_blank" rel="noreferrer"
                             title="Открыть в полном размере">
                            <img
                              src={msg.media_url}
                              alt=""
                              style={{
                                maxWidth: 320,
                                maxHeight: 320,
                                borderRadius: 8,
                                cursor: 'zoom-in',
                                display: 'block',
                              }}
                            />
                          </a>
                        ) : msg.media_type === 'video' ? (
                          // preload="none" — at 30 videos in a chat the
                          // browser otherwise fires 30 metadata requests on
                          // dialog open. User clicks play → loads on demand.
                          <video src={msg.media_url} controls preload="none"
                                 style={{ maxWidth: 320, borderRadius: 8 }} />
                        ) : (msg.media_type === 'voice' || msg.media_type === 'audio') ? (
                          <audio src={msg.media_url} controls preload="none" />
                        ) : (
                          // Documents and other: clickable card with type label
                          // and a separate "Скачать" link that triggers a save
                          // dialog instead of opening inline.
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '8px 12px',
                            background: 'rgba(0,0,0,0.04)',
                            borderRadius: 8,
                          }}>
                            <span style={{ fontSize: 22 }}>📎</span>
                            <a href={msg.media_url} target="_blank" rel="noreferrer"
                               style={{ flex: 1, color: '#3182ce', textDecoration: 'none' }}>
                              {mediaFileName(msg) || msg.media_type || 'attachment'}
                            </a>
                            <a
                              href={msg.media_url}
                              download={mediaFileName(msg) || true}
                               title="Скачать"
                               style={{ fontSize: 18, textDecoration: 'none', color: '#718096' }}>
                              ⬇
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="msg-time" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {!isOut && (
                        <span
                          className={msg.operator_read_at ? 'msg-read-status read' : 'msg-read-status unread'}
                          title={msg.operator_read_at ? 'Прочитано оператором' : 'Не прочитано оператором'}
                        >
                          {msg.operator_read_at ? 'seen' : 'new'}
                        </span>
                      )}
                      {indicator && (
                        <span title={indicator.title} style={{ fontSize: 11, color: indicator.color }}>
                          {indicator.icon}
                        </span>
                      )}
                      {!isDeleted && !isOut && hasRealId && confirmDeleteId !== msg.id && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingMessage(null);
                            setSelectedReply(msg);
                            setTimeout(() => inputRef.current?.focus(), 0);
                          }}
                          title="Ответить на это сообщение"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, color: '#3182ce' }}
                        >
                          ↩
                        </button>
                      )}
                      {isEdited && !isDeleted && (
                        <span style={{ fontSize: 11, color: '#a0aec0' }}>изменено</span>
                      )}
                      {formatTime(msg.sent_at || msg.created_at)}
                      {(editable || deletable) && confirmDeleteId !== msg.id && (
                        <span className="msg-actions" style={{ marginLeft: 8, display: 'inline-flex', gap: 6 }}>
                          {editable && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedReply(null);
                                handleEditMessage(editTarget);
                              }}
                              title="Изменить сообщение"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, color: '#3182ce' }}
                            >
                              ✏️
                            </button>
                          )}
                          {deletable && (
                            <button
                              type="button"
                              onClick={() => handleDeleteMessage(msg)}
                              title="Удалить сообщение"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0, color: '#e53e3e' }}
                            >
                              🗑
                            </button>
                          )}
                        </span>
                      )}
                      {confirmDeleteId === msg.id && (
                        <span style={{ marginLeft: 8, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                          <span style={{ fontSize: 11, color: '#e53e3e' }}>
                            {msg.delivery_state === 'failed' ? 'Убрать?' : 'Удалить?'}
                          </span>
                          <button
                            type="button"
                            onClick={() => confirmDelete(msg)}
                            style={{
                              background: '#e53e3e', color: '#fff', border: 'none',
                              borderRadius: 4, padding: '2px 8px', fontSize: 11, cursor: 'pointer',
                            }}
                          >
                            Да
                          </button>
                          <button
                            type="button"
                            onClick={cancelDelete}
                            style={{
                              background: 'transparent', color: '#718096',
                              border: '1px solid #cbd5e0', borderRadius: 4,
                              padding: '2px 8px', fontSize: 11, cursor: 'pointer',
                            }}
                          >
                            Нет
                          </button>
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {editingMessage && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 16px', margin: '0 16px',
                background: '#ebf8ff', borderLeft: '3px solid #3182ce',
                borderRadius: 6, fontSize: 13,
              }}>
                <span style={{ color: '#3182ce', fontWeight: 600 }}>{'✏️ Изменение'}</span>
                <span style={{
                  color: '#4a5568', flex: 1, whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {editingMessage.text || '[без текста]'}
                </span>
                <button
                  type="button"
                  onClick={cancelEdit}
                  title="Отмена (Esc)"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#718096', fontSize: 18, padding: '0 4px',
                  }}
                >
                  {'×'}
                </button>
              </div>
            )}

            {selectedReply && !editingMessage && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 16px', margin: '0 16px',
                background: '#ebf8ff', borderLeft: '3px solid #3182ce',
                borderRadius: 6, fontSize: 13,
              }}>
                <span style={{ color: '#3182ce', fontWeight: 600 }}>↩ Ответ</span>
                <span style={{
                  color: '#4a5568', flex: 1, whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {selectedReply.sender_name ? `${selectedReply.sender_name}: ` : ''}
                  {selectedReply.text || mediaPlaceholder(selectedReply.media_type) || 'Сообщение'}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedReply(null)}
                  title="Отменить ответ"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#718096', fontSize: 18, padding: '0 4px',
                  }}
                >
                  ×
                </button>
              </div>
            )}

            {Object.keys(typingUsers).length > 0 && (
              <div style={{
                fontSize: 12, color: '#718096', padding: '4px 12px',
                fontStyle: 'italic',
              }}>
                ✏️ {Object.values(typingUsers).map(u => u.name).join(', ')} печатает…
              </div>
            )}
            {pendingAttachments.length > 0 && !editingMessage && (
              <div style={{
                margin: '8px 16px 0',
                padding: 10,
                background: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                  fontSize: 13,
                  color: '#4a5568',
                  fontWeight: 600,
                }}>
                  <span>
                    Файлы к отправке: {pendingAttachments.length}
                  </span>
                  {!sending && !uploadingMedia && (
                    <button
                      type="button"
                      onClick={() => setPendingAttachments([])}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#718096',
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      Очистить
                    </button>
                  )}
                </div>

                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                }}>
                  {pendingAttachments.map((item) => {
                    const mediaType = guessMediaType(item.file);

                    const icon = mediaType === 'photo'
                      ? '🖼️'
                      : mediaType === 'video'
                        ? '🎬'
                        : mediaType === 'audio'
                          ? '🎵'
                          : '📎';

                    return (
                      <div
                        key={item.id}
                        title={item.file.name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 7,
                          maxWidth: 260,
                          padding: '7px 9px',
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: 7,
                          fontSize: 12,
                        }}
                      >
                        <span style={{ fontSize: 17 }}>{icon}</span>

                        <div style={{
                          minWidth: 0,
                          flex: 1,
                        }}>
                          <div style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#2d3748',
                          }}>
                            {item.file.name}
                          </div>
                          <div style={{ color: '#a0aec0' }}>
                            {formatFileSize(item.file.size)}
                          </div>
                        </div>

                        <button
                          type="button"
                          disabled={sending || uploadingMedia}
                          onClick={() => removePendingAttachment(item.id)}
                          title="Убрать файл"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#718096',
                            cursor: sending || uploadingMedia
                              ? 'default'
                              : 'pointer',
                            fontSize: 17,
                            padding: 0,
                          }}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!selectedChannelActive ? (
              <div
                className="msg-input"
                style={{
                  justifyContent: 'center',
                  color: '#718096',
                  fontSize: 13,
                }}
              >
                Аккаунт удалён — история доступна только для чтения.
              </div>
            ) : (
              <form className="msg-input" onSubmit={handleSend}>
              {voiceDraft && !editingMessage && (
                <div className="voice-draft">
                  <div className="voice-draft-title">
                    🎤 Голосовое готово к отправке
                  </div>

                  <audio
                    src={voiceDraft.url}
                    controls
                    preload="metadata"
                  />

                  <div className="voice-draft-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={sending || uploadingMedia}
                      onClick={sendVoiceDraft}
                    >
                      Отправить
                    </button>

                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={sending || uploadingMedia}
                      onClick={discardVoiceDraft}
                    >
                      Удалить
                    </button>

                    <button
                      type="button"
                      className="btn-ghost"
                      disabled={sending || uploadingMedia}
                      onClick={async () => {
                        discardVoiceDraft();
                        await startVoiceRecording();
                      }}
                    >
                      Перезаписать
                    </button>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={handleFilePick}
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
              />

              {!editingMessage && (
                <button
                  type="button"
                  className="btn-ghost msg-icon-btn"
                  title="Добавить файлы"
                  disabled={
                    sending
                    || uploadingMedia
                    || recording
                    || Boolean(voiceDraft)
                  }
                  onClick={() => fileInputRef.current?.click()}
                >
                  📎
                </button>
              )}

              {!editingMessage && (
                <>
                  <button
                    type="button"
                    className={`btn-ghost msg-icon-btn ${recording ? 'recording' : ''}`}
                    title={
                      recording
                        ? 'Остановить и прослушать'
                        : 'Записать голосовое'
                    }
                    disabled={
                      sending
                      || uploadingMedia
                      || Boolean(voiceDraft)
                    }
                    onClick={
                      recording
                        ? stopVoiceRecording
                        : startVoiceRecording
                    }
                  >
                    {recording ? '■' : '🎤'}
                  </button>

                  {recording && (
                    <button
                      type="button"
                      className="btn-ghost msg-icon-btn voice-cancel-recording"
                      title="Отменить запись"
                      disabled={sending || uploadingMedia}
                      onClick={cancelVoiceRecording}
                    >
                      ✕
                    </button>
                  )}
                </>
              )}

              <input
                ref={inputRef}
                type="text"
                placeholder={
                  recording
                    ? 'Идёт запись голосового…'
                    : voiceDraft
                      ? 'Прослушайте запись перед отправкой'
                      : uploadingMedia
                      ? 'Загружаем файл…'
                      : editingMessage
                        ? 'Изменить сообщение…'
                        : 'Введите сообщение…'
                }
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  // Throttle typing ping to once per 3s (rate-limit friendly)
                  const now = Date.now();
                  if (selectedId && (!lastTypingPingRef.current || now - lastTypingPingRef.current > 3000)) {
                    lastTypingPingRef.current = now;
                    sendTyping(selectedId, true).catch(() => {});
                  }
                }}
                onBlur={() => {
                  if (selectedId) sendTyping(selectedId, false).catch(() => {});
                  lastTypingPingRef.current = 0;
                }}
                disabled={
                  sending
                  || uploadingMedia
                  || recording
                  || Boolean(voiceDraft)
                }
                autoFocus
              />
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  sending
                  || uploadingMedia
                  || recording
                  || Boolean(voiceDraft)
                  || (!text.trim() && pendingAttachments.length === 0)
                }
              >
                {(sending || uploadingMedia)
                  ? '...'
                  : editingMessage
                    ? 'Сохранить'
                    : pendingAttachments.length
                      ? `Отправить (${pendingAttachments.length})`
                      : 'Отправить'}
              </button>
              </form>
            )}
          </>
        ) : (
          <div className="msg-no-chat">
            <div className="msg-no-chat-icon">{'\u{1F4AC}'}</div>
            <h3>Выберите диалог</h3>
            <p></p>
          </div>
        )}
      </div>

      {toast && (
        <div
          key={toast.id}
          onClick={() => setToast(null)}
          style={{
            position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
            background: toast.kind === 'error' ? '#c53030' : '#2d3748',
            color: '#fff', padding: '10px 18px', borderRadius: 8,
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)', maxWidth: 480,
            fontSize: 14, zIndex: 1100, cursor: 'pointer',
            animation: 'fadeInUp 200ms ease',
          }}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
